/**
 * The seat invite code (LMS-150) between opening the invite link and confirming enrolment. `/redeem` reads it from the
 * link's `code` query parameter, keeps it here and takes it out of the address bar straight away, so it never reaches
 * analytics, logs or a `Referer`, nor the login redirect a signed-out participant is sent through. Session storage
 * lasts for the tab, so the code survives signing up, verifying an email and signing in there, whether through the
 * login page or the WorkOS hosted flow (LMS-188), and signing out keeps it; opening the email link again, in any
 * browser, starts afresh from the link.
 */

/** The query parameter carrying the code in the invite link (`/redeem?code=...`). */
export const REDEEM_CODE_PARAM = "code";

/** Where a pending code is confirmed; sign-in routes resume here (LMS-188). */
export const REDEEM_PATH = "/redeem";

const STORAGE_KEY = "seat-invite-redeem-code";

/** Set by "Not you? Sign out" on Confirm enrolment, so the page signing out lands on resumes the redeem. */
const RESUME_AFTER_SIGN_OUT_KEY = "seat-invite-redeem-resume-after-sign-out";

export function storeRedeemCode(code: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, code);
  } catch {
    // Storage unavailable (e.g. blocked): the page then asks the participant to open the link again.
  }
}

export function readRedeemCode(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearRedeemCode(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(RESUME_AFTER_SIGN_OUT_KEY);
  } catch {
    // Nothing kept.
  }
}

/** Whether a sign-in route was sent from `/redeem` and a code is waiting there, so it should lead back to it. */
export function isResumingRedeem(redirect: string | null | undefined): boolean {
  return redirect === REDEEM_PATH && readRedeemCode() !== null;
}

/** Where to go once signed in by a route that has no target of its own: back to a pending redeem, if there is one. */
export function signedInLandingPath(fallback: string): string {
  return readRedeemCode() !== null ? REDEEM_PATH : fallback;
}

/**
 * Runs <paramref name="clear"/> (signing out wiping the tab's storage) and puts the pending code back afterwards:
 * signing out must not lose the invite, so the right person can sign in and confirm it (LMS-188).
 */
export function keepRedeemCodeWhile(clear: () => void): void {
  let code: string | null = null;
  let resume: string | null = null;
  try {
    code = sessionStorage.getItem(STORAGE_KEY);
    resume = sessionStorage.getItem(RESUME_AFTER_SIGN_OUT_KEY);
  } catch {
    // Nothing to keep.
  }
  clear();
  try {
    if (code !== null) sessionStorage.setItem(STORAGE_KEY, code);
    if (code !== null && resume !== null) sessionStorage.setItem(RESUME_AFTER_SIGN_OUT_KEY, resume);
  } catch {
    // Storage unavailable: the participant opens the link again.
  }
}

/** Asks the page signing out lands on to go back to Confirm enrolment, for "Not you? Sign out". */
export function resumeRedeemAfterSignOut(): void {
  try {
    sessionStorage.setItem(RESUME_AFTER_SIGN_OUT_KEY, "1");
  } catch {
    // Storage unavailable: the participant opens the link again.
  }
}

/** Forgets a pending "resume after sign-out", as when signing out failed. */
export function cancelResumeRedeemAfterSignOut(): void {
  try {
    sessionStorage.removeItem(RESUME_AFTER_SIGN_OUT_KEY);
  } catch {
    // Nothing kept.
  }
}

/**
 * Whether signing out was asked to resume a pending redeem; asking forgets the request, so it resumes once. False when
 * no code is waiting.
 */
export function takeResumeRedeemAfterSignOut(): boolean {
  try {
    const resume = sessionStorage.getItem(RESUME_AFTER_SIGN_OUT_KEY) !== null;
    sessionStorage.removeItem(RESUME_AFTER_SIGN_OUT_KEY);
    return resume && readRedeemCode() !== null;
  } catch {
    return false;
  }
}
